// The historical dock as a BAR CHART: one bar a row, the name and (where there is one) the icon at the left, the count and its share at the end of the bar. Chris, 2026-10-19: "a bar chart
// option in the historical" - area is hard to compare, a bar carries the count, and a bar has room for the name that a bubble has not. Pure (no DOM, no d3): clusterService.ts feeds it the
// dock's categories and draws what comes back.
import { HistoricalRowsOrder, RowsBox, sortForRows } from "./historicalRows";

export interface BarListItem {
  /** the category's name: unique among the dock's bubbles, and the tie-break so equal counts keep their order */
  id: string;
  value: number;
  /** how wide its (uncut) name draws */
  labelWidth: number;
  /** how wide its count-and-share text draws */
  valueWidth: number;
}

export interface BarListMetrics {
  /** a row is never closer than this (px between two bars' centres) unless there is no room even in the most columns */
  rowMin: number;
  rowMax: number;
  iconW: number;
  gap: number;
  labelMax: number;
  colMin: number;
  colMax: number;
  colGap: number;
  margin: number;
  /** the shortest a bar with something in it is drawn (so one session is visible) */
  barFloor: number;
  /** the least room a bar may have along its row */
  barMinRoom: number;
}

export const BAR_LIST_METRICS: BarListMetrics = { rowMin: 16, rowMax: 30, iconW: 18, gap: 8, labelMax: 170, colMin: 340, colMax: 660, colGap: 44, margin: 12, barFloor: 2, barMinRoom: 40 };

export interface BarListSlot {
  id: string;
  row: number;
  column: number;
  /** the left edge of this bar's column (the icon starts here) */
  x: number;
  /** the row's centre line */
  y: number;
  /** where the name starts, and the most it may draw */
  labelX: number;
  labelW: number;
  /** where the bar starts, and the longest it can be (the largest category's bar is this long) */
  barX: number;
  barMaxW: number;
  /** how thick the bar is */
  barH: number;
  colW: number;
}

export interface BarListLayout {
  slots: Map<string, BarListSlot>;
  columns: number;
  pitch: number;
}

/**
 * Lays the sorted categories out down columns (reading like text: the first at the top of the first column, the next below it, then on to the next column). One column while every bar fits at
 * least `rowMin` apart; more columns when it does not (cities, referrers); and only when even the most columns the box can take are not enough do the rows run below the band, like the rows layout's own
 * overflow. The block is centred in the box. Every column shares one label width, so bars line up across columns.
 */
export const layoutHistoricalBarList = (items: BarListItem[], box: RowsBox, order: HistoricalRowsOrder, m: BarListMetrics = BAR_LIST_METRICS): BarListLayout => {
  const slots = new Map<string, BarListSlot>();
  if (items.length === 0) return { slots, columns: 0, pitch: 0 };
  const byId = new Map(items.map((i) => [i.id, i]));
  const sorted = sortForRows(items.map((i) => ({ id: i.id, value: i.value, size: 0 })), order).map((i) => byId.get(i.id)!);
  const W = Math.max(0, box.right - box.left - 2 * m.margin);
  const H = Math.max(0, box.bottom - box.top - 2 * m.margin);
  const n = sorted.length;

  const maxColumns = Math.max(1, Math.floor((W + m.colGap) / (m.colMin + m.colGap)));
  const rowsFit = Math.max(1, Math.floor(H / m.rowMin));
  const columns = Math.max(1, Math.min(maxColumns, Math.ceil(n / rowsFit)));
  const rowsPerColumn = Math.ceil(n / columns);
  const pitch = Math.max(m.rowMin, Math.min(m.rowMax, H / rowsPerColumn));
  const colW = Math.max(m.colMin > W ? W : m.colMin, Math.min(m.colMax, (W - m.colGap * (columns - 1)) / columns));
  const blockW = columns * colW + m.colGap * (columns - 1);
  const left = box.left + m.margin + Math.max(0, (W - blockW) / 2);
  const blockH = rowsPerColumn * pitch;
  const top = box.top + m.margin + Math.max(0, (H - blockH) / 2);

  const labelW = Math.min(m.labelMax, Math.max(...sorted.map((i) => i.labelWidth)));
  const valueW = Math.max(...sorted.map((i) => i.valueWidth));
  const labelOffset = m.iconW + m.gap;
  const barOffset = labelOffset + labelW + m.gap;
  const barMaxW = Math.max(m.barMinRoom, colW - barOffset - m.gap - valueW);
  const barH = Math.max(8, Math.min(16, Math.round(pitch * 0.6)));

  sorted.forEach((item, i) => {
    const column = Math.floor(i / rowsPerColumn);
    const row = i % rowsPerColumn;
    const x = left + column * (colW + m.colGap);
    slots.set(item.id, { id: item.id, row, column, x, y: top + row * pitch + pitch / 2, labelX: x + labelOffset, labelW, barX: x + barOffset, barMaxW, barH, colW });
  });

  return { slots, columns, pitch };
};

/** a bar's length: its share of the LARGEST category's bar (the biggest fills the room), at least `floor` for anything above zero, snapped to half a pixel so it is only rewritten when it visibly changes */
export const barLength = (value: number, largest: number, barMaxW: number, floor: number = BAR_LIST_METRICS.barFloor): number => {
  if (!(value > 0) || !(largest > 0)) return 0;
  const raw = Math.max(floor, (Math.min(value, largest) / largest) * barMaxW);

  return Math.round(raw * 2) / 2;
};
